import Dropdown from "bootstrap/js/dist/dropdown";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getCases } from "services/api/taptype/cases";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import { apiHandler, getPharmacySegmentFromSegments, isFunction } from "utils/helpers";
import AutoSave from "views/hooks/AutoSave";

const CasesSearchInput = ({ form }) => {
	const { t } = useTranslation();

	// ref hooks
	const dropdownRef = useRef(null);
	const searchInputRef = useRef(null);
	const casesCancelRequestRef = useRef(null);

	// redux global state
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);

	// constants
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const isCurrentSegmentPharmacySegment = pharmacySegment?.id === globals?.segment?.id || false;

	// state hooks
	const [loadMoreCasesLoadingState, setLoadMoreCasesLoadingState] = useState(false);
	const [isLoadingCasesState, setIsLoadingCasesState] = useState(false);
	const [paginationState, setPaginationState] = useState({});
	const [casesState, setCasesState] = useState([]);

	// event handler
	const prepareValueForSearch = (value = "") => value?.trim()?.toLowerCase()?.split(" ");
	const getCasesAPIHandler = async (values, { setErrors, isPagination = false } = {}) => {
		if (isPagination) setLoadMoreCasesLoadingState(true);
		if (!isPagination) setIsLoadingCasesState(true);

		if (casesCancelRequestRef.current?.signal) casesCancelRequestRef.current?.abort();
		casesCancelRequestRef.current = new AbortController();
		await apiHandler(getCases, {
			data: {
				query: values,
				options: { signal: casesCancelRequestRef.current.signal },
			},
			onError: ({ errors }) => {
				if (errors) setErrors(errors);
				if (isPagination) setLoadMoreCasesLoadingState(false);
				if (!isPagination) setIsLoadingCasesState(false);
			},
			onSuccess: async (response) => {
				setPaginationState(response?.meta?.pagination || {});
				setCasesState((prevState) => [...(isPagination ? prevState : []), ...(response?.data || [])]);
				if (isPagination) setLoadMoreCasesLoadingState(false);
				if (!isPagination) setIsLoadingCasesState(false);
				if (searchInputRef?.current && "q" in values) Dropdown.getInstance(dropdownRef.current).show();
			},
		});

		if (searchInputRef?.current && "q" in values) searchInputRef.current.focus();
	};
	const onSearchFormikSubmitHandler = async (values, actions) => getCasesAPIHandler(values, actions);
	const onReadMoreButtonClickHandler = async (values) => getCasesAPIHandler(values, { isPagination: true });
	const onResetClickHandler = async () => {
		searchFormik.resetForm();
		setCasesState([]);
		if (isFunction(form?.setFieldValue)) await form.setFieldValue("case_id", "");
		if (isFunction(form?.setFieldTouched)) await form.setFieldTouched("case_id", true, false);
		await setSearchFormikFieldValue("q", "");
		await searchFormik.setFieldTouched("q", true, false);
		if (dropdownRef?.current) Dropdown.getInstance(dropdownRef.current)?.hide();
		searchFormikSubmitForm();
	};
	const onSelectSingleCaseHandler = async (singleCase) => {
		await searchFormik.setFieldTouched("q", true, false);
		await setSearchFormikFieldValue("q", singleCase?.title || "");
		if (isFunction(form?.setFieldValue)) {
			await form.setFieldValue("case_id", singleCase?.id || "");
			await form.setFieldValue("reason", singleCase?.title || "");
		}
		if (isFunction(form?.setFieldTouched)) {
			await form.setFieldTouched("case_id", true, false);
			await form.setFieldTouched("reason", true, false);
		}
		if (dropdownRef?.current) Dropdown.getInstance(dropdownRef.current)?.hide();
	};

	// formik
	const {
		submitForm: searchFormikSubmitForm,
		setFieldValue: setSearchFormikFieldValue,
		...searchFormik
	} = useFormik({ initialValues: {}, onSubmit: onSearchFormikSubmitHandler });

	// effect hooks
	useEffect(() => {
		searchFormikSubmitForm();
	}, [searchFormikSubmitForm]);

	useEffect(() => {
		const selectedCaseId = form?.values?.case_id || "";
		if (selectedCaseId && casesState.length) {
			const selectedCase = casesState.find(({ id }) => id === selectedCaseId);
			if (selectedCase?.q) setSearchFormikFieldValue("q", selectedCase.q);
		}
	}, [form?.values?.case_id, casesState, setSearchFormikFieldValue]);

	useEffect(() => {
		const dropdownRefCurrent = dropdownRef?.current;
		let dropdown = Dropdown.getInstance(dropdownRefCurrent);
		if (!dropdown) dropdown = new Dropdown(dropdownRefCurrent);
		return () => (dropdown ? Dropdown.getInstance(dropdownRefCurrent).dispose() : false);
	}, []);

	useEffect(() => {
		return () => {
			if (casesCancelRequestRef.current?.signal) casesCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<div className="row gy-3">
			<div className="col-12 position-relative">
				<fieldset>
					<legend className="visually-hidden">search case:</legend>
					<FocusError
						formik={{
							submitForm: searchFormikSubmitForm,
							setFieldValue: setSearchFormikFieldValue,
							...searchFormik,
						}}
					/>
					<AutoSave
						formik={{
							submitForm: searchFormikSubmitForm,
							setFieldValue: setSearchFormikFieldValue,
							...searchFormik,
						}}
						delay={1000}
					/>
					<div
						className={`row gx-0 flex-nowrap align-items-end input-group border-bottom ${
							!!form.touched?.case_id && !!form.errors?.case_id
								? "border-danger"
								: form.values?.case_id && !!form.touched?.case_id && !!!form.errors?.case_id
								? "border-success"
								: ""
						}`}>
						<div className="col">
							<label htmlFor="searchCaseField" className="text-capitalize border-0">
								{`Select ${isCurrentSegmentPharmacySegment ? "Minor Ailment" : "Medical Case"}`}{" "}
								<span className="ms-1">*</span>
							</label>
							<input
								ref={searchInputRef}
								onChange={searchFormik.handleChange}
								value={searchFormik.values?.q || ""}
								type="search"
								name="q"
								id="searchCaseField"
								className={`form-control form-control-sm border-0 rounded-end-0 bg-transparent hide-clear ps-0 ${
									!!form.touched?.case_id && !!form.errors?.case_id
										? "is-invalid"
										: form.values?.case_id && !!form.touched?.case_id && !!!form.errors?.case_id
										? "is-valid"
										: ""
								}`}
								placeholder={`Select ${
									isCurrentSegmentPharmacySegment ? "Minor Ailment" : "Medical Case"
								}`}
								spellCheck="false"
								autoComplete="off"
							/>
						</div>
						<div className="input-group-prepend col-auto ms-1 position-static dropdown">
							{searchFormik.values?.q && (
								<button
									type="button"
									className="btn btn-link btn-sm p-2 rounded-0 text-decoration-none d-inline-flex align-items-center justify-content-center op-50 op-100-hover op-100-focus border-0"
									onClick={onResetClickHandler}
									aria-label={t("actions.close")}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.75rem"
										height="0.75rem"
										fill="currentColor"
										viewBox="0 0 16 16">
										<path d="M1.293 1.293a1 1 0 0 1 1.414 0L8 6.586l5.293-5.293a1 1 0 1 1 1.414 1.414L9.414 8l5.293 5.293a1 1 0 0 1-1.414 1.414L8 9.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L6.586 8 1.293 2.707a1 1 0 0 1 0-1.414z" />
									</svg>
								</button>
							)}
							<button
								type="button"
								className="btn btn-link btn-sm p-2 text-decoration-none d-inline-flex align-items-center justify-content-center border-0 rounded-0"
								onClick={() => searchFormikSubmitForm()}
								disabled={!searchFormik.isValid}>
								{isLoadingCasesState ? (
									<span className="spinner-border spinner-border-sm border-1" role="status">
										<span className="visually-hidden">{`${t("content.loading")}...`}</span>
									</span>
								) : (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.8rem"
										height="0.8rem"
										viewBox="0 0 37.915 37.915"
										fill="currentColor">
										<path d="M16.7,0a16.7,16.7,0,1,0,16.7,16.7A16.716,16.716,0,0,0,16.7,0Zm0,30.312A13.615,13.615,0,1,1,30.312,16.7,13.63,13.63,0,0,1,16.7,30.312Z"></path>
										<g transform="translate(25.996 25.996)">
											<path
												d="M362.513,360.334l-8.837-8.837a1.541,1.541,0,0,0-2.179,2.179l8.837,8.837a1.541,1.541,0,0,0,2.179-2.179Z"
												transform="translate(-351.046 -351.046)"></path>
										</g>
									</svg>
								)}
							</button>
							<button
								ref={dropdownRef}
								className="btn btn-link btn-sm p-2 text-decoration-none d-inline-flex align-items-center justify-content-center border-0 rounded-end-0 dropdown-toggle dropdown-toggle-with-rotate-icon"
								type="button"
								id="searchCaseDropdown"
								data-bs-toggle="dropdown"
								data-bs-display="static"
								data-bs-auto-close="outside"
								aria-expanded="false">
								<span className="btn-icon">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.8rem"
										height="0.8rem"
										fill="currentColor"
										viewBox="0 0 16 16">
										<path
											fillRule="evenodd"
											d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
										/>
									</svg>
								</span>
							</button>
							<ul
								className="dropdown-menu shadow rounded border p-0 w-100 start-0 top-100 mt-1 overflow-x-hidden overflow-y-auto"
								aria-labelledby="searchCaseDropdown"
								style={{ maxHeight: "200px" }}>
								{casesState?.length >= 1 ? (
									<>
										{casesState?.map((singleCase, singleCaseIndex) => (
											<Fragment key={singleCase?.id || singleCaseIndex}>
												<li>
													<button
														className={`dropdown-item white-space-pre-line p-2 ${
															form?.values?.case_id === singleCase?.id ? "active" : ""
														}`}
														type="button"
														onClick={() => onSelectSingleCaseHandler(singleCase)}
														disabled={form.values?.case_id === singleCase?.id}>
														<small className="vstack lh-1">
															<Highlighter
																searchWords={[
																	...(prepareValueForSearch(
																		searchFormik.values?.q || ""
																	) || []),
																]}
																autoEscape={true}
																textToHighlight={singleCase?.title || ""}
																highlightTag={({ children }) => (
																	<strong className="fw-bold">{children}</strong>
																)}
															/>
														</small>
													</button>
												</li>
												{singleCaseIndex + 1 < casesState?.length && (
													<li>
														<div className="dropdown-divider"></div>
													</li>
												)}
											</Fragment>
										))}
										{isLoadingCasesState ? (
											<>
												<li>
													<div className="dropdown-divider"></div>
												</li>
												<li>
													<button
														tabIndex="-1"
														className="btn btn-link btn-sm disabled placeholder-glow d-flex justify-content-center align-items-center col-6 col-md-4 col-lg-6 col-xl-4"
														aria-hidden="true">
														<strong className="placeholder placeholder-sm bg-primary w-50">
															&nbsp;
														</strong>
													</button>
												</li>
											</>
										) : casesState?.length &&
										  paginationState?.total_pages > 1 &&
										  +paginationState?.current_page < paginationState?.total_pages ? (
											<>
												<li>
													<div className="dropdown-divider"></div>
												</li>
												<li>
													<button
														type="button"
														className="btn btn-link btn-sm w-100 text-uppercase"
														onClick={() =>
															onReadMoreButtonClickHandler({
																...searchFormik.values,
																page: +paginationState?.current_page + 1,
															})
														}
														disabled={loadMoreCasesLoadingState}>
														{loadMoreCasesLoadingState ? (
															<span
																className="spinner-border spinner-border-sm"
																role="status">
																<span className="visually-hidden">{`${t(
																	"content.loading"
																)}...`}</span>
															</span>
														) : (
															<strong>{t("actions.load_more")}</strong>
														)}
													</button>
												</li>
											</>
										) : (
											<></>
										)}
									</>
								) : (
									<li className="dropdown-item">
										<span className="vstack fw-bold text-secondary text-center">
											<span className="fs-6 text-capitalize">No Data Found</span>
										</span>
									</li>
								)}
							</ul>
						</div>
					</div>
				</fieldset>
				<input
					value={form.values?.case_id || ""}
					type="text"
					name="case_id"
					className={`form-control form-control-sm rounded-0 border-0 ${
						!!form.touched?.case_id && !!form.errors?.case_id
							? "is-invalid"
							: form.values?.case_id && !!form.touched?.case_id && !!!form.errors?.case_id
							? "is-valid"
							: ""
					}`}
					readOnly
					hidden
					aria-hidden="true"
				/>
				{!!form.touched?.case_id && !!form.errors?.case_id && (
					<div className="invalid-feedback text-uppercase">
						<strong>{form.errors?.case_id}</strong>
					</div>
				)}
			</div>
		</div>
	);
};

export default CasesSearchInput;
